import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Palette, Sparkle, ArrowRight, Check, Image, ArrowLeft,
  FileText, UploadSimple, X, MagicWand, ShareNetwork, Globe
} from '@phosphor-icons/react';
import { normalizeObjectives, redistributeObjectives } from '../lib/objectiveSliders';
import { mapMasterProjectError, resolveMasterProjectErrorMessage } from '../lib/masterProjectErrors';
import { PLATFORM_ICONS } from './project-view/constants';
import WizardDesignDnaV2Step from './project-view/WizardDesignDnaV2Step';
import ContextHelpButton from './ContextHelpButton';
import ClientWorkspaceRecovery from './ClientWorkspaceRecovery';

const INSTRUCTION_SOURCE_DELIMITER = '\n\n---\n\n';

// Temporary compatibility default.
// Format choice will move after Content Master in CONTENT-CORE-001.
// The generation backend still requires project.formats until then; the
// Wizard no longer exposes a format selector, so every new project is
// created with this fixed set (the same default the old selector started
// with) instead of a user choice.
const DEFAULT_WIZARD_FORMATS = ['reel', 'carousel'];

function serializeInstructionSources(sources) {
  return sources
    .filter(source => source?.text?.trim())
    .map(source => `# ${source.name}\n\n${source.text}`)
    .join(INSTRUCTION_SOURCE_DELIMITER);
}

function parseInstructionSources(value) {
  const text = (value || '').trim();
  if (!text) return [];
  return text.split(/\n\n---\n\n/g).filter(Boolean).map((chunk, index) => {
    const match = chunk.match(/^#\s+(.+?)\n\n([\s\S]*)$/);
    const name = match?.[1]?.trim() || `Knowledge source ${index + 1}`;
    const body = match?.[2]?.trim() || chunk.trim();
    return {
      id: `stored-${index}-${name}`,
      name,
      text: body,
      chars: body.length
    };
  });
}

export default function Wizard({ setActiveView, setSelectedProject, resumeData, setWizardResumeData, onCancel }) {
  const { api } = useAuth();
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 0: client identity first, then the project/campaign brief.
  // A Client Brain is the durable client profile; a Project is one campaign
  // or execution context that points to it. Keeping the two names distinct
  // here prevents the historical "project name == client name" coupling.
  const [clientSetupMode, setClientSetupMode] = useState('new');
  const [clientName, setClientName] = useState('');
  const [clientBrains, setClientBrains] = useState([]);
  const [clientBrainsLoading, setClientBrainsLoading] = useState(false);
  const [selectedExistingClientId, setSelectedExistingClientId] = useState('');
  const [selectedClientWorkspace, setSelectedClientWorkspace] = useState(null);
  const [inheritedClientTov, setInheritedClientTov] = useState(false);
  const [editInheritedTov, setEditInheritedTov] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [sector, setSector] = useState('');
  const [description, setDescription] = useState('');
  const [contentLanguage, setContentLanguage] = useState((i18n.language || 'it').split('-')[0]);
  const [awareness, setAwareness] = useState(60);
  const [education, setEducation] = useState(30);
  const [monetizing, setMonetizing] = useState(10);
  const handleObjectiveChange = (changedKey, rawValue) => {
    const next = redistributeObjectives({ awareness, education, monetizing }, changedKey, rawValue);
    setAwareness(next.awareness);
    setEducation(next.education);
    setMonetizing(next.monetizing);
  };
  const [durationWeeks, setDurationWeeks] = useState(1);
  const [geo, setGeo] = useState('');
  const [briefNotes, setBriefNotes] = useState('');
  const [projectInstructions, setProjectInstructions] = useState('');
  const [instructionSources, setInstructionSources] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [clientBrainId, setClientBrainId] = useState(null);
  // MASTER-FIRST-PRO-001: project mode is automatic at creation time.
  // Entitled Pro users always create canonical Master projects; Base users
  // keep the historical Social workflow. Existing projects restore their
  // immutable stored mode and are never converted on resume.
  const [projectMode, setProjectMode] = useState('social');
  // PROJECT-MODE-FOUNDATION-001A: true only once the /plan/entitlements
  // request has settled (success OR failure) — never re-set to false
  // afterward. While this is false, project creation must be blocked
  // entirely: an entitled Pro user on a slow request could otherwise
  // create an immutable Social project before the Master card ever has
  // a chance to appear.
  const [projectModeEntitlementsResolved, setProjectModeEntitlementsResolved] = useState(false);

  // Step 1: Optional social connections (project-scoped — the OAuth start
  // route requires project_id, so this step only ever loads/acts once
  // projectId exists, i.e. after Brief has created the project). Purely
  // optional: connect/skip, never a publishing configuration screen.
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [socialProfiles, setSocialProfiles] = useState([]);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialError, setSocialError] = useState('');
  const [socialNotice, setSocialNotice] = useState('');
  const [connectingPlatformId, setConnectingPlatformId] = useState(null);
  // Holds { popup, handler, pollTimer } for the single in-flight OAuth
  // attempt, so every listener/timer started by connectSocial can be torn
  // down from one place (finishOauthSession) — on success, on error, on
  // manual popup close, and on unmount.
  const oauthSessionRef = useRef(null);

  // Step 2: Optional visual DNA
  const [designExamples, setDesignExamples] = useState([]);
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const [analyzingDesign, setAnalyzingDesign] = useState(false);
  const [designDna, setDesignDna] = useState(null);

  // Step 3: ToV
  const [tovPreset, setTovPreset] = useState('');
  const [formality, setFormality] = useState(5);
  const [energy, setEnergy] = useState(5);
  const [empathy, setEmpathy] = useState(5);
  const [humor, setHumor] = useState(3);
  const [storytelling, setStorytelling] = useState(5);
  const [customInstructions, setCustomInstructions] = useState('');
  const [captionLength, setCaptionLength] = useState('medium');
  const [brandKeywords, setBrandKeywords] = useState('');
  const [forbiddenWords, setForbiddenWords] = useState('');
  const [signaturePhrases, setSignaturePhrases] = useState('');

  // Step 4: Content generation (hooks are generated/persisted internally,
  // never shown or approved by the user). Phase-aware so a retry after a
  // failure — or a resume that finds persisted hooks — only re-runs what
  // hasn't already succeeded. See generateHooksAndContent /
  // generateContentFromPersistedHooks / loadGeneratedProject /
  // retryFailedPhase / openProject below.
  const [genProgress, setGenProgress] = useState(0);
  const [openingProject, setOpeningProject] = useState(false);
  // 'idle' (not yet attempted; the normal first-time flow after ToV save) |
  // 'hooks_ready' (resumed with wizard_step >= 3 and no content yet — hooks
  //   are already generated and persisted, so the CTA must call only
  //   content/generate + GET, never hooks/generate again) |
  // 'hooks_error' (hooks/generate failed — retry re-runs the whole chain) |
  // 'content_error' (hooks persisted, content/generate raised/rejected —
  //   retry skips hooks and only re-calls content/generate, since nothing
  //   tells us the persisted hooks themselves are at fault) |
  // 'content_all_rejected' (hooks persisted, content/generate returned a
  //   structured 200 success:false/count:0 — every hook in the batch was
  //   rejected. BASE-COST-FALLBACK-001 (PR#45 second follow-up): count:0/
  //   success:false does NOT by itself mean the persisted hooks are at
  //   fault — the same shape is also produced when every hook instead fails
  //   on a provider/credits error or a content-field ToV violation the AI
  //   could still fix on a fresh attempt, neither of which regenerating
  //   hooks would help, and both of which would burn an extra AI call
  //   altering hooks that were perfectly fine. So retry is driven by the
  //   backend's own contentRetryStrategyRef signal (data.retry_strategy,
  //   captured in generateContentFromPersistedHooks): only an explicit
  //   'regenerate_hooks' — meaning the backend confirmed every rejection in
  //   the batch is confined to the immutable hook_text field — runs the
  //   full chain again (generateHooksAndContent); anything else (including
  //   a missing/unrecognized signal, safest default) retries content only,
  //   same as content_error) |
  // 'project_load_error' (content persisted, final GET failed — retry
  //   only reloads the project, no AI endpoint is called again) |
  // 'done'. loading===true covers the in-progress state for all of them.
  const [contentGenState, setContentGenState] = useState('idle');
  // See 'content_all_rejected' above — the backend's classification of the
  // most recent all-rejected batch, read only by retryFailedPhase. A ref
  // (not state) because it never drives a render on its own: the derived
  // error message is set imperatively via setError at the same time.
  const contentRetryStrategyRef = useRef(null);
  const contentGenHasError = contentGenState === 'hooks_error'
    || contentGenState === 'content_error'
    || contentGenState === 'content_all_rejected'
    || contentGenState === 'project_load_error'
    || contentGenState === 'master_generation_error'
    || contentGenState === 'master_generation_in_progress'
    || contentGenState === 'master_generation_inconsistent'
    || contentGenState === 'master_completion_outcome_unknown';
  const contentChainRef = useRef(false);

  const steps = [
    { label: t('wizard.steps.brief'), icon: Sparkle },
    { label: t('wizard.steps.social'), icon: ShareNetwork },
    { label: t('wizard.steps.design'), icon: Image },
    { label: t('wizard.steps.tone'), icon: Palette },
    { label: t('wizard.steps.content'), icon: Sparkle },
  ];

  const presets = [
    { id: 'professionale', label: t('wizard.presets.professionale'), f: 8, e: 4, em: 5, h: 2, s: 4 },
    { id: 'amichevole', label: t('wizard.presets.amichevole'), f: 3, e: 7, em: 8, h: 6, s: 7 },
    { id: 'ispirazionale', label: t('wizard.presets.ispirazionale'), f: 5, e: 9, em: 7, h: 3, s: 8 },
    { id: 'provocatorio', label: t('wizard.presets.provocatorio'), f: 4, e: 9, em: 3, h: 7, s: 6 },
    { id: 'educativo', label: t('wizard.presets.educativo'), f: 7, e: 5, em: 6, h: 3, s: 5 },
  ];

  useEffect(() => {
    if (!resumeData) return;
    const { project, tov } = resumeData;
    setProjectId(project.id);
    setClientBrainId(project.client_brain_id || null);
    // PROJECT-MODE-FOUNDATION-001: a missing/legacy value resumes as
    // "social" (matching the backend's own compatibility normalization);
    // a stored "master" is preserved as-is. Since project_mode is
    // immutable, this is a read-only restore — the selector never
    // re-renders for an already-created project (step 0 is only ever
    // reached before creation), so this value is never offered for
    // conversion here.
    setProjectMode(project.project_mode || 'social');
    setProjectName(project.name || '');
    setSector(project.sector || '');
    setDescription(project.description || '');
    setContentLanguage((project.language || i18n.language || 'it').split('-')[0]);
    const objectives = project.objectives || {};
    const normalizedObjectives = normalizeObjectives({
      awareness: project.objective_awareness ?? objectives.awareness ?? 60,
      education: project.objective_education ?? objectives.education ?? 30,
      monetizing: project.objective_monetizing ?? objectives.monetizing ?? 10,
    });
    setAwareness(normalizedObjectives.awareness);
    setEducation(normalizedObjectives.education);
    setMonetizing(normalizedObjectives.monetizing);
    // project.formats is intentionally not re-applied to any user-controlled
    // state here: the Wizard no longer exposes a format selector, and this
    // resume path must never rewrite a project's already-persisted formats.
    setDurationWeeks(project.duration_weeks || 1);
    setGeo(project.geo || '');
    setBriefNotes(project.brief_notes || '');
    const parsedInstructionSources = parseInstructionSources(project.custom_instructions || '');
    setInstructionSources(parsedInstructionSources);
    setProjectInstructions(project.custom_instructions || '');
    if (tov) {
      setTovPreset(tov.preset || '');
      setFormality(tov.formality ?? 5);
      setEnergy(tov.energy ?? 5);
      setEmpathy(tov.empathy ?? 5);
      setHumor(tov.humor ?? 3);
      setStorytelling(tov.storytelling ?? 5);
      setCustomInstructions(tov.custom_instructions || '');
      setCaptionLength(tov.caption_length || 'medium');
      setBrandKeywords(tov.brand_keywords || '');
      setForbiddenWords(tov.forbidden_words || '');
      setSignaturePhrases(tov.signature_phrases || '');
    }
    // New visible indexes: 0 Brief, 1 Social, 2 Visual DNA, 3 Tone of Voice,
    // 4 Content. Backend wizard_step semantics are unchanged (0 created,
    // 1 personas, 2 tov saved, 3 hooks generated, 4 content generated) and
    // have no dedicated Social step — resuming an existing project never
    // forces the user back through the optional Social step (it's only
    // reached via createProject() in the current session); it resumes
    // straight to Visual DNA or later, per project.wizard_step.
    const wizardStep = project.wizard_step || 0;
    const visibleStepByBackendStep = wizardStep >= 3 ? 4 : wizardStep >= 1 ? 3 : project.id ? 2 : 0;
    setStep(visibleStepByBackendStep);
    // MASTER-PROJECT-WIZARD-INTEGRATION-001 (task section 6): a Master
    // project resumes through an entirely separate branch — it is NEVER
    // classified as 'hooks_ready' (there is no separate hooks phase for
    // Master, and /content/generate must never be called just because
    // wizard_step >= 3), and its own `master_generation_state` field (set
    // by the canonical generation route's atomic claim lifecycle) drives
    // the resume state instead of the Social hooks/content heuristic.
    if ((project.project_mode || 'social') === 'master') {
      const masterGenerationState = project.master_generation_state;
      if ((project.content_count || 0) > 0 && project.status && project.status !== 'draft') {
        setContentGenState('done');
        setGenProgress(project.content_count || 0);
      } else if (masterGenerationState === 'complete') {
        setContentGenState('done');
        setGenProgress(project.content_count || 0);
      } else if (masterGenerationState === 'generating') {
        setContentGenState('master_generation_in_progress');
      } else if (!masterGenerationState || masterGenerationState === 'failed') {
        setContentGenState('idle');
      } else {
        // Any other persisted value (an unknown/corrupted state) is
        // never silently retried — a controlled error state instead.
        setContentGenState('master_generation_inconsistent');
      }
    } else if ((project.content_count || 0) > 0 || (project.status && project.status !== 'draft')) {
      // Defensive Content-step state (Social, unchanged):
      // - content already generated (content_count > 0 or status no
      //   longer draft) -> 'done', showing the completed screen with an
      //   Open Project action rather than an incorrect "not started" CTA.
      // - wizard_step >= 3 with no content yet -> hooks are already
      //   generated and persisted (wizard_step 3), or content generation
      //   was attempted but never completed (wizard_step 4). Either way
      //   hooks must not be regenerated, so this resumes as
      //   'hooks_ready': the CTA calls only content/generate + GET,
      //   never hooks/generate again.
      // - otherwise (wizard_step < 3) -> 'idle', the normal first-time
      //   flow after ToV save is what generates hooks.
      setContentGenState('done');
      setGenProgress(project.content_count || 0);
    } else if (wizardStep >= 3) {
      setContentGenState('hooks_ready');
    } else {
      setContentGenState('idle');
    }
    if (setWizardResumeData) setWizardResumeData(null);
  }, [resumeData, i18n.language, setWizardResumeData]);

  // Load reusable Client Brains for the identity selector at the very start
  // of a fresh project. Resume flows already carry a linked client id and do
  // not need this discovery request.
  useEffect(() => {
    if (resumeData) return;
    let cancelled = false;
    setClientBrainsLoading(true);
    api.get('/client-brains')
      .then(({ data }) => {
        if (!cancelled) setClientBrains(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setClientBrains([]);
      })
      .finally(() => {
        if (!cancelled) setClientBrainsLoading(false);
      });
    return () => { cancelled = true; };
  }, [api, resumeData]);

  // Selecting an existing client establishes the durable identity first and
  // pre-fills the business-level fields. Project-specific objectives, notes,
  // duration and instructions remain independent below.
  useEffect(() => {
    if (clientSetupMode !== 'existing' || !selectedExistingClientId) {
      setSelectedClientWorkspace(null);
      return;
    }
    const selected = clientBrains.find(item => item.id === selectedExistingClientId);
    if (!selected) return;
    setClientName(selected.name || '');
    setSector(selected.sector || '');
    setDescription(selected.description || '');
    setContentLanguage((selected.language || i18n.language || 'it').split('-')[0]);
    let cancelled = false;
    api.get(`/client-brains/${selectedExistingClientId}/workspace`)
      .then(({ data }) => { if (!cancelled) setSelectedClientWorkspace(data || null); })
      .catch(() => { if (!cancelled) setSelectedClientWorkspace(null); });
    return () => { cancelled = true; };
  }, [api, clientBrains, clientSetupMode, selectedExistingClientId, i18n.language]);

  // MASTER-FIRST-PRO-001: resolve the server-owned entitlement once.
  // A fresh entitled Pro wizard is forced to Master; a fresh non-entitled
  // wizard stays Social. Resume flows preserve the project's immutable mode.
  // Creation remains blocked until resolution so a slow request cannot create
  // the wrong immutable mode. Fail closed to Social.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/plan/entitlements');
        if (!cancelled && !resumeData) {
          setProjectMode(Boolean(data?.entitlements?.pro_editorial_engine) ? 'master' : 'social');
        }
      } catch (e) {
        if (!cancelled && !resumeData) setProjectMode('social');
      } finally {
        if (!cancelled) setProjectModeEntitlementsResolved(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    api.get(`/projects/${projectId}/design`)
      .then(({ data }) => {
        if (cancelled) return;
        setDesignExamples(data.examples || []);
        setDesignDna(data.active_dna || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, projectId]);

  // Cancels every listener/timer a connectSocial() call started, whichever
  // way it ends — success, error, manual popup close, or unmount — so no
  // stale 'message' listener or polling interval is ever left stacked.
  const finishOauthSession = useCallback(() => {
    const session = oauthSessionRef.current;
    if (!session) return;
    window.removeEventListener('message', session.handler);
    clearInterval(session.pollTimer);
    oauthSessionRef.current = null;
  }, []);

  useEffect(() => () => finishOauthSession(), [finishOauthSession]);

  // Canonical loader for both platforms and profiles — used on mount (once
  // projectId exists) and by the inline Retry action. Social status is
  // purely additive UI: a failure here must never block Skip, Continue, or
  // any later Wizard step, so it only ever sets socialError, never the
  // Wizard's shared `error`/content-generation state.
  const loadSocialStatus = useCallback(async () => {
    if (!projectId) return;
    setSocialLoading(true);
    setSocialError('');
    try {
      const [platformsRes, profilesRes] = await Promise.all([
        api.get('/social/platforms'),
        api.get(`/social/project/${projectId}`),
      ]);
      setSocialPlatforms(platformsRes.data || []);
      setSocialProfiles(profilesRes.data || []);
    } catch (e) {
      setSocialError(t('wizard.social.loadError'));
    } finally {
      setSocialLoading(false);
    }
    // `t`'s identity isn't a meaningful dependency here — this only fetches
    // social status, it doesn't need to re-run just because the
    // translation function was recreated (e.g. on language change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectId]);

  useEffect(() => {
    loadSocialStatus();
  }, [loadSocialStatus]);

  // The backend response is always the source of truth for connections —
  // after a successful OAuth round trip this reloads it instead of
  // appending the postMessage payload directly to local state.
  const reloadSocialProfiles = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/social/project/${projectId}`);
      setSocialProfiles(data || []);
    } catch (e) {
      // Non-blocking: the connection itself already succeeded: keep
      // whatever profiles were last loaded rather than surfacing an error
      // for what is now just a stale-list refresh.
    }
  }, [api, projectId]);

  const connectSocial = async (platform) => {
    if (connectingPlatformId) return;
    setSocialNotice('');
    setSocialError('');
    setConnectingPlatformId(platform.id);
    try {
      const { data } = await api.get(`/social/oauth/start/${platform.id}?project_id=${encodeURIComponent(projectId)}`);
      const popup = window.open(data.auth_url, 'wizard_oauth_popup', 'width=600,height=700,left=200,top=100');
      if (!popup) {
        setConnectingPlatformId(null);
        setSocialNotice(t('wizard.social.popupBlocked'));
        return;
      }
      const handler = (event) => {
        const session = oauthSessionRef.current;
        if (session?.popup && event.source && event.source !== session.popup) return;
        if (event.data?.type === 'oauth_success') {
          if (event.data?.project_id !== projectId) return;
          finishOauthSession();
          popup.close();
          reloadSocialProfiles();
          setConnectingPlatformId(null);
          setSocialNotice(t('wizard.social.connectionSuccess'));
        } else if (event.data?.type === 'oauth_error') {
          finishOauthSession();
          popup.close();
          setConnectingPlatformId(null);
          setSocialError(t('wizard.social.connectionFailed'));
        }
      };
      window.addEventListener('message', handler);
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          finishOauthSession();
          setConnectingPlatformId(null);
          setSocialNotice(t('wizard.social.connectionCancelled'));
        }
      }, 500);
      oauthSessionRef.current = { popup, handler, pollTimer };
    } catch (e) {
      setConnectingPlatformId(null);
      setSocialError(t('wizard.social.connectionFailed'));
    }
  };

  const applyPreset = (p) => {
    setTovPreset(p.id);
    setFormality(p.f); setEnergy(p.e); setEmpathy(p.em); setHumor(p.h); setStorytelling(p.s);
  };

  const updateInstructionSources = (nextSources) => {
    setInstructionSources(nextSources);
    setProjectInstructions(serializeInstructionSources(nextSources));
  };

  const uploadInstructionSources = async (files) => {
    if (!files.length) return;
    setUploadingFile(true);
    try {
      const parsed = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/projects/parse-instructions-file', fd);
        parsed.push({
          id: `${file.name}-${file.size}-${Date.now()}-${parsed.length}`,
          name: file.name,
          text: data.text || '',
          chars: (data.text || '').length,
          parser: data.parser || 'legacy',
          format: data.format || ''
        });
      }
      updateInstructionSources([...instructionSources, ...parsed]);
    } catch (err) {
      setError(t('wizard.errors.fileRead', { message: err.response?.data?.detail || err.message }));
    } finally {
      setUploadingFile(false);
    }
  };

  const uploadDesignExamples = async (files) => {
    if (!files.length || !projectId) return;
    setUploadingDesign(true);
    setError('');
    try {
      const form = new FormData();
      files.forEach(file => form.append('files', file));
      const { data } = await api.post(`/projects/${projectId}/design/examples`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDesignExamples(prev => [...prev, ...(data.examples || [])]);
    } catch (e) {
      setError(t('wizard.design.uploadError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setUploadingDesign(false);
    }
  };

  const analyzeAndActivateDesign = async () => {
    if (!projectId || designExamples.length < 2) return;
    setAnalyzingDesign(true);
    setError('');
    try {
      const { data } = await api.post(`/projects/${projectId}/design/analyze`);
      setDesignDna({ ...data, status: 'active' });
    } catch (e) {
      setError(t('wizard.design.analysisError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setAnalyzingDesign(false);
    }
  };

  const removeDesignExample = async (exampleId) => {
    if (!projectId) return;
    setError('');
    try {
      await api.delete(`/projects/${projectId}/design/examples/${exampleId}`);
      setDesignExamples(prev => prev.filter(example => example.id !== exampleId));
    } catch (e) {
      setError(t('wizard.design.deleteError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const createProject = async () => {
    // PROJECT-MODE-FOUNDATION-001A: synchronous guard — never rely on the
    // disabled button alone. Blocks a direct click/event/keyboard submit
    // from creating a Client Brain, importing/analyzing its documents,
    // the project itself, or personas, until /plan/entitlements has
    // settled (success or failure). Without this, an entitled Pro user
    // on a slow request could create an immutable Social project before
    // the Master card ever had a chance to appear.
    if (!projectModeEntitlementsResolved) return;
    const missingClientIdentity = clientSetupMode === 'new'
      ? !clientName.trim()
      : !selectedExistingClientId;
    if (missingClientIdentity || !projectName.trim() || !sector.trim()) {
      setError(t('wizard.errors.requiredFields'));
      return;
    }
    setLoading(true); setError('');
    try {
      let linkedBrainId = selectedExistingClientId;
      let inheritedSetup = null;

      if (clientSetupMode === 'new') {
        const brainPayload = {
          name: clientName.trim(),
          sector: sector.trim(),
          description: description.trim(),
          language: contentLanguage,
          custom_instructions: projectInstructions,
          editorial_rules: briefNotes.trim(),
          tags: DEFAULT_WIZARD_FORMATS,
        };

        const brainResponse = await api.post('/client-brains', brainPayload);
        linkedBrainId = brainResponse.data?.id;

        if (linkedBrainId && instructionSources.length) {
          await api.post(`/client-brains/${linkedBrainId}/documents/import`, {
            documents: instructionSources
              .filter(source => source?.text?.trim())
              .map(source => ({
                name: source.name || clientName.trim(),
                text: source.text,
                chars: source.chars || source.text.length,
                parser: source.parser || '',
                format: source.format || '',
              })),
          });
        }

        if (linkedBrainId) {
          try {
            await api.post(`/client-brains/${linkedBrainId}/analyze-documents`, { force_refresh: true });
          } catch (brainAnalyzeError) {
            console.warn('Client brain analysis failed, continuing with project creation', brainAnalyzeError);
          }
        }
      }

      setClientBrainId(linkedBrainId || null);
      const { data } = await api.post('/projects', {
        name: projectName, sector, description, language: contentLanguage, objective_awareness: awareness,
        objective_education: education, objective_monetizing: monetizing,
        formats: DEFAULT_WIZARD_FORMATS, duration_weeks: durationWeeks, geo, brief_notes: briefNotes,
        custom_instructions: projectInstructions,
        client_brain_id: linkedBrainId || null,
        project_mode: projectMode,
      });

      // Existing client: copy its reusable defaults into the new project so
      // the normal project-scoped readers (ToV, Design DNA, Brand Kit) keep
      // working unchanged. The full Wizard remains a review/edit path; the
      // Dashboard quick-start is still the explicit skip-and-generate path.
      if (clientSetupMode === 'existing' && linkedBrainId) {
        try {
          const { data: inherited } = await api.post(`/projects/${data.id}/client-workspace/inherit`);
          inheritedSetup = inherited;
          if (inherited?.has_tov) {
            setInheritedClientTov(true);
            setEditInheritedTov(false);
            const { data: inheritedTov } = await api.get(`/tov/${data.id}`);
            setTovPreset(inheritedTov.preset || '');
            setFormality(inheritedTov.formality ?? 5);
            setEnergy(inheritedTov.energy ?? 5);
            setEmpathy(inheritedTov.empathy ?? 5);
            setHumor(inheritedTov.humor ?? 3);
            setStorytelling(inheritedTov.storytelling ?? 5);
            setCustomInstructions(inheritedTov.custom_instructions || '');
            setCaptionLength(inheritedTov.caption_length || 'medium');
            setBrandKeywords(inheritedTov.brand_keywords || '');
            setForbiddenWords(inheritedTov.forbidden_words || '');
            setSignaturePhrases(inheritedTov.signature_phrases || '');
          }
        } catch (inheritError) {
          console.warn('Client workspace inheritance failed; continuing with editable Wizard', inheritError);
        }
      }

      setProjectId(data.id);
      try {
        await api.post('/personas/generate', { project_id: data.id });
      } catch (personaError) {
        console.warn('Background personas generation failed', personaError);
      }
      // Reused client: review existing voice rather than walking through
      // social connections, reference uploads and ToV sliders again.
      setStep(clientSetupMode === 'existing' && (inheritedSetup?.has_tov || inheritedSetup?.has_design_v3_templates) ? 3 : 1);
    } catch (e) {
      setError(e.response?.data?.detail || t('wizard.errors.projectCreation'));
    } finally { setLoading(false); }
  };

  // Hooks are generated and persisted internally by /hooks/generate itself
  // (it replaces the project's stored hooks and never needs a follow-up
  // /hooks/save — that endpoint only re-persists the same data with new
  // ids and is not called from the Wizard anymore). /content/generate is
  // then called with just the project_id and reads those persisted hooks
  // itself. Each phase tracks its own failure state so a retry never
  // repeats an AI call whose result is already persisted — in particular,
  // if the final project GET fails after content was already generated,
  // retrying must only re-run that GET, never /hooks/generate or
  // /content/generate again (that would duplicate AI usage and content).
  // Master projects never touch /hooks/generate or /content/generate — a
  // single server-owned route generates the whole canonical batch. This is
  // the one call site reached both from saveTovAndGenerateContent (first
  // attempt) and from retryFailedPhase's generic fallback, so branching
  // here is sufficient to keep Master out of every Base generation route.
  const generateMasterProjectContent = async () => {
    let count;
    try {
      const { data } = await api.post('/pro/master-projects/generate', { project_id: projectId });
      count = data.count || 0;
    } catch (e) {
      const mapped = mapMasterProjectError(e);
      if (mapped.code === 'master_project_generation_in_progress') {
        setContentGenState('master_generation_in_progress');
      } else if (mapped.code === 'master_project_completion_outcome_unknown') {
        setContentGenState('master_completion_outcome_unknown');
      } else if (mapped.code === 'master_project_generation_inconsistent') {
        setContentGenState('master_generation_inconsistent');
      } else {
        setContentGenState('master_generation_error');
      }
      setError(resolveMasterProjectErrorMessage(e, t));
      return;
    }
    setGenProgress(count);
    await loadGeneratedProject();
  };

  const generateHooksAndContent = async () => {
    if (projectMode === 'master') {
      return generateMasterProjectContent();
    }
    try {
      await api.post('/hooks/generate', { project_id: projectId });
    } catch (e) {
      setContentGenState('hooks_error');
      setError(t('wizard.errors.hooksGeneration', { message: e.response?.data?.detail || e.message }));
      return;
    }
    await generateContentFromPersistedHooks();
  };

  const generateContentFromPersistedHooks = async () => {
    let count;
    let success;
    let data;
    try {
      ({ data } = await api.post('/content/generate', { project_id: projectId }));
      count = data.count || 0;
      // HOTFIX-TOV-RETRY-ISOLATION-001 (post-merge #44) taught the backend
      // to report a structured success:false when every hook was rejected
      // (count===0) so the caller can distinguish that from a genuine
      // partial/full success without depending on the HTTP status code,
      // which stays 200 either way. success may be absent on older mocks/
      // responses — only an explicit false, or count===0, is a failure.
      success = data.success !== false && count > 0;
    } catch (e) {
      setContentGenState('content_error');
      setError(t('wizard.errors.contentGeneration', { message: e.response?.data?.detail || e.message }));
      return;
    }
    if (!success) {
      // Distinct from the catch block above: this is a structured
      // all-rejected result, not a network/exception failure, and per
      // BASE-COST-FALLBACK-001 the cause can be an immutable hook_text
      // violation OR a provider/generic/content-field failure — only the
      // backend can tell them apart (see contentGenState docs above), so
      // its explicit signal drives the retry, never count/success alone.
      const strategy = data.retry_strategy === 'regenerate_hooks' ? 'regenerate_hooks' : 'retry_content';
      contentRetryStrategyRef.current = strategy;
      setContentGenState('content_all_rejected');
      setError(t(
        strategy === 'regenerate_hooks'
          ? 'wizard.errors.contentGenerationEmpty'
          : 'wizard.errors.contentGenerationEmptyRetryContent'
      ));
      return;
    }
    setGenProgress(count);
    await loadGeneratedProject();
  };

  const loadGeneratedProject = async () => {
    try {
      const { data: proj } = await api.get(`/projects/${projectId}`);
      const isMaster = (proj.project_mode || projectMode) === 'master';

      if (isMaster) {
        const state = proj.master_generation_state;
        const count = proj.content_count || 0;
        // A successful project GET is NOT proof that Master generation
        // completed. Deploy/crash recovery can leave the project readable
        // while the generation claim is still active (or failed) and the
        // contents collection is empty. Never mark that state as done or
        // route into Design DNA.
        if (state === 'complete' && count > 0 && proj.status === 'active') {
          setContentGenState('done');
          setGenProgress(count);
          setSelectedProject(proj);
          setActiveView('dna');
          return;
        }
        if (state === 'generating') {
          setContentGenState('master_generation_in_progress');
        } else if (!state || state === 'failed') {
          setContentGenState('master_generation_error');
        } else {
          setContentGenState('master_generation_inconsistent');
        }
        return;
      }

      setContentGenState('done');
      setSelectedProject(proj);
      setActiveView('content');
    } catch (e) {
      setContentGenState('project_load_error');
      setError(t('wizard.errors.projectLoad', { message: e.response?.data?.detail || e.message }));
    }
  };

  // Shared by the main flow (after ToV save) and by the Content step's own
  // Generate/Retry button — contentChainRef prevents either entry point
  // from launching a second overlapping chain.
  const runGuardedPhase = async (phaseFn) => {
    if (contentChainRef.current) return;
    contentChainRef.current = true;
    setLoading(true);
    setError('');
    try {
      await phaseFn();
    } finally {
      setLoading(false);
      contentChainRef.current = false;
    }
  };

  // Runs whichever phase hasn't succeeded yet, based on the current
  // contentGenState — 'idle'/'hooks_error' run the full chain (hooks were
  // never persisted or failed), 'hooks_ready'/'content_error' skip hooks
  // because they're already persisted and the failure wasn't attributed to
  // them, 'content_all_rejected' skips hooks too UNLESS the backend
  // explicitly flagged the persisted hooks themselves as the unfixable
  // cause (contentRetryStrategyRef.current === 'regenerate_hooks' — see its
  // docs above), and 'project_load_error' only reloads the project.
  const retryFailedPhase = () => runGuardedPhase(() => {
    if (projectMode === 'master') {
      // In-progress is safe to send back through the canonical route:
      // the backend rejects a live claim and can recover a stale deploy/
      // crash claim. Inconsistent/unknown-outcome states remain reload-only.
      if (contentGenState === 'master_generation_in_progress') {
        // The backend owns stale-claim recovery. Retrying the canonical
        // generation route is safe: a genuinely live claim still returns
        // 409, while an abandoned deploy/crash claim can be recovered.
        return generateMasterProjectContent();
      }
      if (
        contentGenState === 'project_load_error'
        || contentGenState === 'master_generation_inconsistent'
        || contentGenState === 'master_completion_outcome_unknown'
      ) {
        return loadGeneratedProject();
      }
      return generateMasterProjectContent();
    }
    if (
      contentGenState === 'hooks_ready'
      || contentGenState === 'content_error'
      || (contentGenState === 'content_all_rejected' && contentRetryStrategyRef.current !== 'regenerate_hooks')
    ) {
      return generateContentFromPersistedHooks();
    }
    if (contentGenState === 'project_load_error') return loadGeneratedProject();
    return generateHooksAndContent();
  });

  const keepInheritedTovAndGenerate = async () => {
    if (contentChainRef.current || !projectId) return;
    contentChainRef.current = true;
    setError('');
    setStep(4);
    contentChainRef.current = false;
    // The already materialized project ToV is authoritative: do not post
    // /tov/save here, because that would unnecessarily overwrite it.
    await runGuardedPhase(generateHooksAndContent);
  };

  const saveTovAndGenerateContent = async () => {
    if (contentChainRef.current) return;
    contentChainRef.current = true;
    setLoading(true);
    setError('');
    try {
      await api.post('/tov/save', {
        project_id: projectId, preset: tovPreset, formality, energy, empathy,
        humor, storytelling, custom_instructions: customInstructions, caption_length: captionLength,
        brand_keywords: brandKeywords, forbidden_words: forbiddenWords, signature_phrases: signaturePhrases
      });
    } catch (e) {
      setError(t('wizard.errors.tovSave'));
      setLoading(false);
      contentChainRef.current = false;
      return;
    }
    setStep(4);
    contentChainRef.current = false;
    await runGuardedPhase(generateHooksAndContent);
  };

  // Used by the completed Content step ('done') to navigate into the
  // project. Never touches hooks/generate or content/generate — a GET
  // failure here just surfaces an error and leaves the button in place so
  // the user can try loading the project again.
  //
  // SOCIAL-RECONNECT-REMINDER-001: the "no social connected" reminder is
  // NOT triggered here anymore — it's centralized in App.js on every
  // selectedProject change (dashboard resume, hard refresh rehydration,
  // AND this wizard completion path all funnel through setSelectedProject
  // there), so it fires consistently regardless of how the project was
  // opened, not just right after a fresh wizard run.
  const openProject = async () => {
    if (openingProject) return;
    setOpeningProject(true);
    setError('');
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setSelectedProject(data);
      // Resuming/completing a Master wizard follows the same guided entry:
      // Design DNA first, then the user can continue to Content.
      setActiveView((data.project_mode || projectMode) === 'master' ? 'dna' : 'content');
    } catch (e) {
      setError(t('wizard.errors.projectLoad', { message: e.response?.data?.detail || e.message }));
    } finally {
      setOpeningProject(false);
    }
  };

  // Step 4 copy/action resolution for Master projects — kept out of the
  // JSX below so the render stays a plain ternary chain per state, never
  // exposing internal terms (claim/batch id/Planner/Writer/Mongo). The
  // specific localized error text (in-progress/inconsistent/unknown
  // outcome) is already surfaced by the shared error banner via `error`
  // (set in generateMasterProjectContent through resolveMasterProjectErrorMessage);
  // the title/subtitle here only need to reflect the coarse phase.
  const isMasterMode = projectMode === 'master';
  const masterStep4Title = loading
    ? t('wizard.masterContent.generatingTitle')
    : contentGenState === 'done'
    ? t('wizard.masterContent.completedTitle')
    : t('wizard.masterContent.readyTitle');
  const masterStep4Subtitle = loading
    ? t('wizard.masterContent.generatingSubtitleIdeas')
    : contentGenState === 'done'
    ? t('wizard.masterContent.completedSubtitle', { count: genProgress })
    : t('wizard.masterContent.readySubtitle');
  const masterStep4ShowsCheckStatus = !loading && (
    contentGenState === 'master_generation_in_progress'
    || contentGenState === 'master_generation_inconsistent'
    || contentGenState === 'master_completion_outcome_unknown'
  );

  const workspaceDefaults = selectedClientWorkspace?.workspace_defaults || {};
  const selectedClientHasTov = Boolean(workspaceDefaults.tov && Object.keys(workspaceDefaults.tov).length);
  const selectedClientHasDesign = (
    (Array.isArray(workspaceDefaults.design_v3_templates) && workspaceDefaults.design_v3_templates.length > 0)
    || Boolean(workspaceDefaults.design_dna_v2 && Object.keys(workspaceDefaults.design_dna_v2).length)
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress */}
      <div className="wizard-progress mb-4">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center">
            <div className={`wizard-step-item ${step === i ? 'active' : step > i ? 'completed' : ''}`}>
              <div className="wizard-step-number">
                {step > i ? <Check weight="bold" size={14} /> : i + 1}
              </div>
              <span className="text-sm hidden sm:inline">{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`wizard-step-line ${step > i ? 'active' : ''}`} />}
          </div>
        ))}
      </div>
      <div className="progress-bar mb-8">
        <div className="progress-fill" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
      </div>

      {error && <div className="text-sm text-[var(--accent-pink)] mb-4 p-3 rounded-lg" style={{ background: 'rgba(236,72,153,0.1)' }}>{error}</div>}

      <AnimatePresence mode="wait">
        {/* STEP 0: Client identity + Brief */}
        {step === 0 && (
          <motion.div key="brief" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-2xl font-bold">{t('wizard.brief.title')}</h2>
              <ContextHelpButton articleId="wizard-project" label="Guida Wizard" />
            </div>
            <p className="text-[var(--text-secondary)] mb-6 text-sm">{t('wizard.brief.subtitle')}</p>

            <div className="card mb-6" data-testid="wizard-client-identity-card">
              <div className="mb-4">
                <p className="text-sm font-semibold">{t('wizard.clientIdentity.title', { defaultValue: 'Prima definiamo il cliente' })}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {t('wizard.clientIdentity.subtitle', { defaultValue: 'Il cliente è il profilo permanente. Il progetto/campagna viene creato sotto quel cliente e può avere obiettivi e brief propri.' })}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  data-testid="wizard-client-new"
                  aria-pressed={clientSetupMode === 'new'}
                  className="card text-left w-full"
                  style={clientSetupMode === 'new' ? {
                    borderColor: 'var(--gradient-start)',
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(236, 72, 153, 0.16) 100%)',
                  } : undefined}
                  onClick={() => {
                    setClientSetupMode('new');
                    setSelectedExistingClientId('');
                    setSelectedClientWorkspace(null);
                    setClientName('');
                    setSector('');
                    setDescription('');
                  }}
                >
                  <div className="font-semibold mb-1">{t('wizard.clientIdentity.newTitle', { defaultValue: 'Nuovo cliente' })}</div>
                  <p className="text-xs text-[var(--text-secondary)]">{t('wizard.clientIdentity.newDescription', { defaultValue: 'Creo un nuovo profilo cliente e il suo primo progetto.' })}</p>
                </button>
                <button
                  type="button"
                  data-testid="wizard-client-existing"
                  aria-pressed={clientSetupMode === 'existing'}
                  className="card text-left w-full"
                  style={clientSetupMode === 'existing' ? {
                    borderColor: 'var(--gradient-start)',
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(236, 72, 153, 0.16) 100%)',
                  } : undefined}
                  onClick={() => setClientSetupMode('existing')}
                >
                  <div className="font-semibold mb-1">{t('wizard.clientIdentity.existingTitle', { defaultValue: 'Cliente esistente' })}</div>
                  <p className="text-xs text-[var(--text-secondary)]">{t('wizard.clientIdentity.existingDescription', { defaultValue: 'Collego il progetto a un cliente già salvato e riuso il suo setup.' })}</p>
                </button>
              </div>

              {clientSetupMode === 'new' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('wizard.clientIdentity.clientName', { defaultValue: 'Nome cliente' })} *</label>
                    <input
                      data-testid="wizard-client-name"
                      className="input-dark"
                      placeholder={t('wizard.clientIdentity.clientNamePlaceholder', { defaultValue: 'Es. Studio Rossi, Andrea Bianchi…' })}
                      value={clientName}
                      onChange={e => setClientName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('wizard.brief.sectorLabel')} *</label>
                    <input data-testid="wizard-sector" className="input-dark" placeholder={t('wizard.brief.sectorPlaceholder')} value={sector} onChange={e => setSector(e.target.value)} style={{ paddingLeft: '1rem' }} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('wizard.clientIdentity.clientDescription', { defaultValue: 'Descrizione cliente / business' })}</label>
                    <textarea className="input-dark" rows={3} placeholder={t('wizard.brief.descriptionPlaceholder')} value={description} onChange={e => setDescription(e.target.value)} style={{ paddingLeft: '1rem' }} />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('wizard.clientIdentity.selectExisting', { defaultValue: 'Seleziona cliente' })} *</label>
                    <select
                      data-testid="wizard-existing-client-select"
                      className="input-dark w-full"
                      value={selectedExistingClientId}
                      onChange={e => setSelectedExistingClientId(e.target.value)}
                      disabled={clientBrainsLoading}
                    >
                      <option value="">{clientBrainsLoading
                        ? t('common.loading')
                        : t('wizard.clientIdentity.selectPlaceholder', { defaultValue: 'Seleziona un cliente…' })}</option>
                      {clientBrains.map(client => (
                        <option key={client.id} value={client.id}>{client.name}{client.sector ? ` · ${client.sector}` : ''}</option>
                      ))}
                    </select>
                    {!clientBrainsLoading && clientBrains.length === 0 && (
                      <p className="text-xs text-[var(--text-muted)] mt-2">{t('wizard.clientIdentity.noneFound', { defaultValue: 'Non ci sono ancora clienti salvati. Scegli “Nuovo cliente”.' })}</p>
                    )}
                  </div>
                  {selectedExistingClientId && (
                    <div className="rounded-xl p-3 text-xs" style={{ background: 'rgba(124,92,255,.08)', border: '1px solid rgba(124,92,255,.18)' }}>
                      <p className="font-semibold">{clientName || t('wizard.clientIdentity.existingTitle', { defaultValue: 'Cliente esistente' })}</p>
                      {sector && <p className="text-[var(--text-muted)] mt-1">{sector}</p>}
                      <p className="text-[var(--text-muted)] mt-2">
                        {selectedClientHasDesign ? '✓ Design DNA' : '— Design DNA'} · {selectedClientHasTov ? '✓ Tone of Voice' : '— Tone of Voice'}
                      </p>
                      <p className="text-[var(--text-muted)] mt-1">{t('wizard.clientIdentity.inheritHint', { defaultValue: 'Il setup disponibile verrà copiato nel nuovo progetto; qui potrai comunque rivederlo e modificarlo.' })}</p>
                    </div>
                  )}
                  {selectedExistingClientId && (
                    <ClientWorkspaceRecovery api={api} clientId={selectedExistingClientId}
                      workspace={selectedClientWorkspace} onRecovered={setSelectedClientWorkspace} />
                  )}
                </div>
              )}
            </div>

            {/* MASTER-FIRST-PRO-001: project mode is entitlement-driven.
                No Social/Master choice is shown during creation. */}
            <div className="card space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">{t('wizard.clientIdentity.projectName', { defaultValue: 'Nome progetto / campagna' })} *</label>
                <input data-testid="wizard-project-name" className="input-dark" placeholder={t('wizard.brief.projectNamePlaceholder')} value={projectName} onChange={e => setProjectName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">{t('wizard.language')}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'it', label: 'Italiano' },
                    { id: 'en', label: 'English' },
                    { id: 'es', label: 'Español' },
                    { id: 'fr', label: 'Français' },
                  ].map(lang => (
                    <button
                      key={lang.id}
                      type="button"
                      className={`preset-btn ${contentLanguage === lang.id ? 'active' : ''}`}
                      onClick={() => setContentLanguage(lang.id)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('wizard.brief.geoLabel')}</label>
                <input className="input-dark" placeholder={t('wizard.brief.geoPlaceholder')} value={geo} onChange={e => setGeo(e.target.value)} style={{ paddingLeft: '1rem' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">{t('wizard.brief.campaignDurationLabel')}</label>
                <div className="flex gap-2">
                  {[{ w: 1, l: t('wizard.brief.durations.oneWeek') }, { w: 2, l: t('wizard.brief.durations.twoWeeks') }, { w: 4, l: t('wizard.brief.durations.oneMonth') }].map(d => (
                    <button key={d.w} className={`preset-btn ${durationWeeks === d.w ? 'active' : ''}`} onClick={() => setDurationWeeks(d.w)}>
                      {d.l} <span className="text-xs opacity-60 ml-1">{t('wizard.brief.contentsCount', { count: d.w * 7 })}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-3">{t('wizard.brief.objectivesLabel')}</label>
                <div className="space-y-3 rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)' }}>
                  {[
                    { id: 'awareness', label: t('wizard.brief.objectives.awareness'), value: awareness, color: 'var(--accent-blue)' },
                    { id: 'education', label: t('wizard.brief.objectives.education'), value: education, color: 'var(--accent-green)' },
                    { id: 'monetizing', label: t('wizard.brief.objectives.monetizing'), value: monetizing, color: 'var(--accent-orange)' },
                  ].map(obj => (
                    <div key={obj.id}>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="font-medium">{obj.label}</span>
                        <strong style={{ color: obj.color }}>{obj.value}%</strong>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={obj.value}
                        onChange={e => handleObjectiveChange(obj.id, e.target.value)}
                        className="slider-dark"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-2">{t('wizard.brief.objectivesHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('wizard.brief.aiNotesLabel')}</label>
                <textarea className="input-dark" rows={3} placeholder={t('wizard.brief.aiNotesPlaceholder')} value={briefNotes} onChange={e => setBriefNotes(e.target.value)} style={{ paddingLeft: '1rem' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('wizard.brief.customInstructionsLabel')} <span className="text-[var(--text-muted)] font-normal">({t('wizard.brief.optional')})</span></label>
                <p className="text-xs text-[var(--text-muted)] mb-2">{t('wizard.brief.customInstructionsHint')}</p>
                {instructionSources.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                      <span>{t('wizard.brief.knowledgeSourcesReady', { count: instructionSources.length })}</span>
                      <span>{t('wizard.brief.charactersCount', { count: projectInstructions.length })}</span>
                    </div>
                    {instructionSources.map(source => (
                      <div key={source.id} className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)' }}>
                        <FileText size={16} color="var(--accent-purple)" />
                        <span className="text-sm flex-1 truncate text-[var(--accent-purple)]">{source.name}</span>
                        <span className="text-xs text-[var(--text-muted)]">{t('wizard.brief.charactersCount', { count: source.chars || source.text?.length || 0 })}</span>
                        <button
                          type="button"
                          className="p-1 hover:text-red-400 transition-colors"
                          onClick={() => updateInstructionSources(instructionSources.filter(item => item.id !== source.id))}
                          aria-label={t('wizard.brief.removeKnowledgeSource')}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label
                  className="flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed var(--border-color)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-purple)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                  onDragOver={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'var(--accent-purple)';
                  }}
                  onDragLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                  onDrop={async e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    await uploadInstructionSources(Array.from(e.dataTransfer.files || []));
                  }}
                >
                  <UploadSimple size={16} color="var(--text-muted)" />
                  <span className="text-sm text-[var(--text-muted)]">{uploadingFile ? t('wizard.brief.fileAnalyzing') : t('wizard.brief.fileUploadCta')}</span>
                  <input type="file" className="hidden" accept=".txt,.md,.html,.htm,.docx,.pdf,.pptx,.xlsx,.xls,.csv,.json,.xml,.zip" multiple
                    onChange={async e => {
                      const files = Array.from(e.target.files || []);
                      await uploadInstructionSources(files);
                      e.target.value = '';
                    }} />
                </label>
                {instructionSources.length > 0 && (
                  <p className="text-xs text-[var(--text-muted)] mt-2">{t('wizard.brief.knowledgeSourcesHint')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3" data-testid="wizard-brief-actions">
              <button
                type="button"
                className="btn-ghost flex-shrink-0"
                onClick={onCancel}
                data-testid="wizard-cancel-btn"
              >
                <X size={16} /> {t('wizard.actions.cancel', 'Annulla')}
              </button>
              <button
                data-testid="wizard-next-btn"
                className="btn-gradient ml-auto flex-shrink-0"
                onClick={createProject}
                disabled={loading || !projectModeEntitlementsResolved}
              >
                {loading || !projectModeEntitlementsResolved ? t('wizard.generating') : t('wizard.complete')} <ArrowRight weight="bold" size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 1: Optional social connections. Project-scoped (the OAuth
            start route requires project_id), reachable only once the Brief
            step has created the project. Connect/skip only — never a
            publishing configuration screen; advanced management stays in
            the project's Social tab. */}
        {step === 1 && (
          <motion.div key="social" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold">{t('wizard.social.title')}</h2>
              <span className="badge purple">{t('wizard.design.optional')}</span>
            </div>
            <p className="text-[var(--text-secondary)] mb-6 text-sm">{t('wizard.social.subtitle')}</p>

            {socialError && (
              <div
                className="text-sm mb-4 p-3 rounded-lg flex items-center justify-between gap-3"
                style={{ background: 'rgba(236,72,153,0.1)', color: 'var(--accent-pink)' }}
              >
                <span>{socialError}</span>
                <button
                  data-testid="wizard-social-retry-btn"
                  className="btn-ghost text-xs py-1 px-2 flex-shrink-0"
                  onClick={loadSocialStatus}
                >
                  {t('wizard.actions.retry')}
                </button>
              </div>
            )}
            {socialNotice && (
              <div className="text-sm mb-4 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                {socialNotice}
              </div>
            )}

            {socialLoading ? (
              <p className="text-sm text-[var(--text-muted)] mb-6">{t('wizard.social.loading')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {socialPlatforms.filter(platform => !platform.is_tool).map(platform => {
                  const pi = PLATFORM_ICONS[platform.id] || { Icon: Globe, color: '#fff' };
                  const platformProfiles = socialProfiles.filter(profile => profile.platform === platform.id);
                  const oauthProfiles = platformProfiles.filter(
                    profile => profile.connection_mode === 'oauth' && profile.connected !== false
                  );
                  const manualProfiles = platformProfiles.filter(profile => profile.connection_mode !== 'oauth');
                  const hasWarning = oauthProfiles.some(profile => profile.connection_warning);
                  const isConnecting = connectingPlatformId === platform.id;
                  let statusLabel;
                  let statusColor;
                  if (!platform.configured) {
                    statusLabel = t('wizard.social.notAvailable');
                    statusColor = 'var(--text-muted)';
                  } else if (oauthProfiles.length > 0) {
                    statusLabel = t('common.connected');
                    statusColor = '#22c55e';
                  } else {
                    statusLabel = t('common.notConnected');
                    statusColor = 'var(--text-muted)';
                  }
                  let connectLabel;
                  if (hasWarning) connectLabel = t('common.reconnect');
                  else if (oauthProfiles.length > 0) connectLabel = t('wizard.social.addAccount');
                  else connectLabel = t('common.connect');
                  return (
                    <div key={platform.id} className="card" data-testid={`wizard-social-platform-${platform.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <pi.Icon weight="fill" size={22} color={pi.color} />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm">{platform.name}</p>
                          <p className="text-[10px]" style={{ color: statusColor }}>{statusLabel}</p>
                        </div>
                      </div>
                      {oauthProfiles.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {oauthProfiles.map(profile => (
                            <div
                              key={profile.id}
                              className="text-xs truncate px-2 py-1 rounded"
                              style={{ background: 'rgba(34,197,94,0.1)' }}
                            >
                              {profile.profile_name}
                            </div>
                          ))}
                        </div>
                      )}
                      {manualProfiles.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {manualProfiles.map(profile => (
                            <div
                              key={profile.id}
                              className="text-xs truncate px-2 py-1 rounded flex items-center justify-between gap-2"
                              style={{ background: 'rgba(255,255,255,0.04)' }}
                            >
                              <span className="truncate">{profile.profile_name}</span>
                              <span className="text-[9px] text-[var(--text-muted)] flex-shrink-0">
                                {t('wizard.social.manualProfile')}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {platform.configured ? (
                        <button
                          data-testid={`wizard-social-connect-${platform.id}`}
                          className="btn-ghost text-xs py-1.5 w-full"
                          onClick={() => connectSocial(platform)}
                          disabled={!!connectingPlatformId}
                        >
                          {isConnecting ? t('wizard.actions.saving') : connectLabel}
                        </button>
                      ) : (
                        <p className="text-[10px] text-[var(--text-muted)]">{t('wizard.social.notAvailable')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-[var(--text-muted)] mb-6">{t('wizard.social.manageHint')}</p>

            <div className="flex gap-3">
              <button data-testid="wizard-social-skip-btn" className="btn-ghost" onClick={() => setStep(2)}>
                {t('wizard.design.skip')}
              </button>
              <button data-testid="wizard-social-continue-btn" className="btn-gradient" onClick={() => setStep(2)}>
                {t('wizard.design.continue')} <ArrowRight weight="bold" size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 2: Design DNA V2 — typed reference slots */}
        {step === 2 && (
          <motion.div
            key="design"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <WizardDesignDnaV2Step
              api={api}
              projectId={projectId}
              onBack={() => setStep(1)}
              onContinue={() => setStep(3)}
            />
          </motion.div>
        )}

        {/* STEP 3: Tone of Voice */}
        {step === 3 && (
          <motion.div key="tone" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {inheritedClientTov && !editInheritedTov ? (
              <div className="card p-6" data-testid="wizard-inherited-tov-choice">
                <h2 className="text-xl font-bold mb-3">{t('wizard.existingClientReuse.tovPrompt')}</h2>
                <p className="text-sm text-[var(--text-muted)] mb-5">{t('wizard.existingClientReuse.tovHint')}</p>
                <div className="flex gap-3 flex-wrap">
                  <button type="button" className="btn-gradient" onClick={keepInheritedTovAndGenerate} disabled={loading} data-testid="wizard-keep-existing-tov">
                    {t('wizard.existingClientReuse.keepAndGenerate')}
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => setEditInheritedTov(true)} disabled={loading} data-testid="wizard-edit-existing-tov">
                    {t('wizard.existingClientReuse.editTov')}
                  </button>
                </div>
              </div>
            ) : (
              <>
            <h2 className="text-2xl font-bold mb-2">{t('wizard.tov.title')}</h2>
            <p className="text-[var(--text-secondary)] mb-6 text-sm">{t('wizard.tov.subtitle')}</p>
            <div className="flex gap-2 mb-8 flex-wrap">
              {presets.map(p => (
                <button key={p.id} className={`preset-btn ${tovPreset === p.id ? 'active' : ''}`} onClick={() => applyPreset(p)}>{p.label}</button>
              ))}
            </div>
            <div className="card mb-6">
              {[
                { label: t('wizard.tov.sliders.formality.label'), hint: t('wizard.tov.sliders.formality.hint'), val: formality, set: setFormality },
                { label: t('wizard.tov.sliders.energy.label'), hint: t('wizard.tov.sliders.energy.hint'), val: energy, set: setEnergy },
                { label: t('wizard.tov.sliders.empathy.label'), hint: t('wizard.tov.sliders.empathy.hint'), val: empathy, set: setEmpathy },
                { label: t('wizard.tov.sliders.humor.label'), hint: t('wizard.tov.sliders.humor.hint'), val: humor, set: setHumor },
                { label: t('wizard.tov.sliders.storytelling.label'), hint: t('wizard.tov.sliders.storytelling.hint'), val: storytelling, set: setStorytelling },
              ].map(s => (
                <div key={s.label} className="tone-slider-row">
                  <div className="tone-slider-label">
                    <span className="font-medium">{s.label}</span>
                    <span>{s.hint} <strong className="ml-2">{s.val}</strong></span>
                  </div>
                  <input type="range" min="1" max="10" value={s.val} onChange={e => s.set(Number(e.target.value))} className="slider-dark" />
                </div>
              ))}
            </div>
            <div className="card mb-6">
              <label className="block text-sm font-medium mb-2">{t('wizard.tov.instructionsLabel')}</label>
              <textarea className="input-dark" rows={3} placeholder={t('wizard.tov.instructionsPlaceholder')} value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} style={{ paddingLeft: '1rem' }} />
            </div>
            <div className="card mb-6">
              <label className="block text-sm font-medium mb-2">{t('wizard.tov.brandKeywordsLabel')}</label>
              <p className="text-xs text-[var(--text-muted)] mb-2">{t('wizard.tov.listFieldHint')}</p>
              <textarea
                data-testid="wizard-tov-brand-keywords"
                className="input-dark"
                rows={2}
                placeholder={t('wizard.tov.brandKeywordsPlaceholder')}
                value={brandKeywords}
                onChange={e => setBrandKeywords(e.target.value)}
                style={{ paddingLeft: '1rem' }}
              />
            </div>
            <div className="card mb-6">
              <label className="block text-sm font-medium mb-2">{t('wizard.tov.forbiddenWordsLabel')}</label>
              <p className="text-xs text-[var(--text-muted)] mb-2">{t('wizard.tov.listFieldHint')}</p>
              <textarea
                data-testid="wizard-tov-forbidden-words"
                className="input-dark"
                rows={2}
                placeholder={t('wizard.tov.forbiddenWordsPlaceholder')}
                value={forbiddenWords}
                onChange={e => setForbiddenWords(e.target.value)}
                style={{ paddingLeft: '1rem' }}
              />
            </div>
            <div className="card mb-6">
              <label className="block text-sm font-medium mb-2">{t('wizard.tov.signaturePhrasesLabel')}</label>
              <p className="text-xs text-[var(--text-muted)] mb-2">{t('wizard.tov.listFieldHint')}</p>
              <textarea
                data-testid="wizard-tov-signature-phrases"
                className="input-dark"
                rows={2}
                placeholder={t('wizard.tov.signaturePhrasesPlaceholder')}
                value={signaturePhrases}
                onChange={e => setSignaturePhrases(e.target.value)}
                style={{ paddingLeft: '1rem' }}
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">{t('wizard.tov.captionLengthLabel')}</label>
              <div className="flex gap-2">
                {[{ id: 'short', l: t('wizard.tov.captionLengths.short.label'), d: t('wizard.tov.captionLengths.short.hint') }, { id: 'medium', l: t('wizard.tov.captionLengths.medium.label'), d: t('wizard.tov.captionLengths.medium.hint') }, { id: 'long', l: t('wizard.tov.captionLengths.long.label'), d: t('wizard.tov.captionLengths.long.hint') }].map(c => (
                  <button key={c.id} className={`preset-btn ${captionLength === c.id ? 'active' : ''}`} onClick={() => setCaptionLength(c.id)}>
                    {c.l} <span className="text-xs opacity-60 block">{c.d}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button className="btn-ghost" onClick={() => setStep(2)}><ArrowLeft size={16} /> {t('wizard.actions.back')}</button>
              <button data-testid="wizard-tov-save-btn" className="btn-gradient" onClick={saveTovAndGenerateContent} disabled={loading}>
                {loading ? t('wizard.actions.saving') : t('wizard.tov.saveAndGenerateContent')} <ArrowRight weight="bold" size={18} />
              </button>
            </div>
              </>
            )}
          </motion.div>
        )}

        {/* STEP 4: Content generation. Hooks are generated internally
            (generateHooksAndContent) — the user only ever sees this
            single step: loading -> progress, error -> message + phase-aware
            Retry, success -> automatic navigation to the project. */}
        {step === 4 && (
          <motion.div key="content" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[var(--gradient-start)] to-[var(--gradient-end)] flex items-center justify-center">
              <Sparkle weight="fill" size={40} color="white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {isMasterMode
                ? masterStep4Title
                : loading
                ? t('wizard.content.generatingTitle')
                : contentGenState === 'done'
                ? t('wizard.content.completedTitle')
                : t('wizard.content.readyTitle')}
            </h2>
            <p className="text-[var(--text-secondary)] mb-8 text-sm">
              {isMasterMode
                ? masterStep4Subtitle
                : loading
                ? t('wizard.content.generatingSubtitle')
                : contentGenState === 'done'
                ? t('wizard.content.completedSubtitle', { count: genProgress })
                : t('wizard.content.readySubtitle')}
            </p>
            {loading && (
              <div className="max-w-md mx-auto mb-8">
                <div className="progress-bar">
                  <motion.div className="progress-fill" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 30, ease: 'linear' }} />
                </div>
              </div>
            )}
            {!loading && contentGenState !== 'done' && (
              <button data-testid="generate-content-btn" className="btn-gradient" onClick={retryFailedPhase} disabled={loading}>
                {isMasterMode
                  ? (masterStep4ShowsCheckStatus ? t('wizard.masterContent.checkStatusCta') : (contentGenHasError ? t('wizard.actions.retry') : t('wizard.masterContent.generateCta')))
                  : (contentGenHasError ? t('wizard.actions.retry') : t('wizard.content.generateCta'))} <ArrowRight weight="bold" size={18} />
              </button>
            )}
            {!loading && contentGenState === 'done' && (
              <button data-testid="open-project-btn" className="btn-gradient" onClick={openProject} disabled={openingProject}>
                {isMasterMode ? t('wizard.masterContent.openProjectCta') : t('wizard.content.openProjectCta')} <ArrowRight weight="bold" size={18} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
